import { ONBOARDING_STAGES, OnboardingStage } from '@/types/onboarding';
import { CheckCircle2, Circle, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface StageProgressBarProps {
  stages: typeof ONBOARDING_STAGES;
  currentStage: OnboardingStage | string;
  onStageClick?: (stagePath: string) => void;
  disabled?: boolean;
}

const StageProgressBar = ({ stages, currentStage, onStageClick, disabled = false }: StageProgressBarProps) => {
  // Map database stage names to stage numbers
  const stageNameToNumber: Record<string, number> = {
    'initiated': 1,
    'data_import': 2,
    'validation': 3,
    'mapping': 4,
    'configuration': 5,
    'review': 6,
    'go_live': 7,
    'completed': 7, // Completed is also stage 7
  };

  // Calculate current stage index for progress bar
  const getCurrentStageIndex = () => {
    // First try to match by stage number from path (e.g., 'stage-2')
    const pathMatch = stages.findIndex(
      s => s.path === currentStage || s.path.replace('stage-', '') === currentStage.replace('stage-', '').replace('_', '-')
    );
    if (pathMatch !== -1) return pathMatch;

    // Then try to match by stage name (e.g., 'data_import')
    const stageNumber = stageNameToNumber[currentStage.toLowerCase()];
    if (stageNumber) {
      const index = stages.findIndex(s => s.number === stageNumber);
      if (index !== -1) return index;
    }

    // Try to match by stage name pattern
    const stageNameMatch = stages.findIndex(s => {
      const normalizedStageName = s.name.toLowerCase().replace(/\s+/g, '_');
      return normalizedStageName === currentStage.toLowerCase() || 
             normalizedStageName.replace('_', '-') === currentStage.toLowerCase().replace('_', '-');
    });
    if (stageNameMatch !== -1) return stageNameMatch;

    return 0; // Default to first stage
  };

  const currentStageIndex = getCurrentStageIndex();
  const progressPercentage = ((currentStageIndex + 1) / stages.length) * 100;

  const getStageStatus = (stageNumber: number) => {
    // If we couldn't find the current stage index, try to match by stage name
    if (currentStageIndex === -1) {
      const dbStageNumber = stageNameToNumber[currentStage.toLowerCase()];
      if (dbStageNumber) {
        if (stageNumber < dbStageNumber) return 'completed';
        if (stageNumber === dbStageNumber) return 'current';
        return 'upcoming';
      }
      // Default: if stage is 'initiated', all are upcoming except stage 1
      if (currentStage.toLowerCase() === 'initiated') {
        if (stageNumber === 1) return 'current';
        return 'upcoming';
      }
      return 'upcoming';
    }

    // Normal comparison based on current stage index
    if (stageNumber < currentStageIndex + 1) return 'completed';
    if (stageNumber === currentStageIndex + 1) return 'current';
    return 'upcoming';
  };

  return (
    <div className="w-full space-y-4">
      {/* Progress Bar with Percentage */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-muted-foreground">Overall Progress</span>
          <span className="font-bold text-primary">{Math.round(progressPercentage)}%</span>
        </div>
        <div className="w-full bg-secondary rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-500"
            style={{
              width: `${progressPercentage}%`,
            }}
          />
        </div>
      </div>

      {/* Stage Indicators */}
      <div className="flex items-center justify-between relative">
        {/* Progress Line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-border -z-10">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{
              width: `${progressPercentage}%`,
            }}
          />
        </div>

        {/* Stage Indicators */}
        {stages.map((stage, index) => {
          const status = getStageStatus(stage.number);
          // Allow clicking on current stage, completed stages, and the next stage (for forward navigation)
          const isClickable = !disabled && onStageClick && (
            status === 'completed' || 
            status === 'current' || 
            index === currentStageIndex + 1 // Allow clicking on next stage for forward navigation
          );

          return (
            <div
              key={stage.number}
              className={cn(
                'flex flex-col items-center flex-1 relative z-10',
                isClickable && 'cursor-pointer'
              )}
              onClick={() => isClickable && onStageClick?.(stage.path)}
            >
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Stage Circle */}
                    <div
                      className={cn(
                        'w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all',
                        status === 'completed' && 'bg-green-500 border-green-500 text-white',
                        status === 'current' && 'bg-pink-500 border-pink-500 text-white ring-4 ring-pink-500/20',
                        status === 'upcoming' && 'bg-background border-muted-foreground/30 text-muted-foreground',
                        isClickable && 'hover:scale-110 cursor-pointer',
                        !isClickable && status === 'upcoming' && 'opacity-50'
                      )}
                    >
                      {status === 'completed' ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : status === 'current' ? (
                        <Circle className="h-5 w-5 fill-current" />
                      ) : !isClickable ? (
                        <Lock className="h-4 w-4" />
                      ) : (
                        <Circle className="h-5 w-5" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="font-medium">{stage.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {status === 'completed' && '✓ Completed'}
                      {status === 'current' && '⚙ In Progress'}
                      {status === 'upcoming' && (isClickable ? 'Click to navigate' : 'Locked - Complete previous stages')}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Stage Label */}
              <div className="mt-2 text-center max-w-[120px]">
                <div
                  className={cn(
                    'text-xs font-medium',
                    status === 'completed' && 'text-green-600',
                    status === 'current' && 'text-pink-600 font-semibold',
                    status === 'upcoming' && 'text-muted-foreground'
                  )}
                >
                  Stage {stage.number}
                </div>
                <div
                  className={cn(
                    'text-xs mt-0.5',
                    status === 'current' ? 'text-pink-600 font-medium' : 'text-muted-foreground'
                  )}
                >
                  {stage.name.split(' ')[0]}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StageProgressBar;

